import {useState, useEffect, useCallback, useMemo, useRef} from 'react'
import {createPortal} from 'react-dom'
import {useLocation} from 'react-router-dom'
import axios from 'axios'
import {Modal as BsModal} from 'bootstrap'
import {useAuth} from '../../modules/auth'
import {getAdminToken} from '../../modules/admin/auth/_requests'
import {tutorials, TutorialStep} from './tutorialSteps'
import './SpotlightTutorial.scss'

const API_URL = process.env.REACT_APP_API_URL
const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN

// Customer-service WhatsApp, same number the post-register wizard uses.
// 0722210190 → international (drop the leading 0, prepend 972).
const TUTORIAL_SUPPORT_WA = '972722210190'

const occupationOptions = [
  { value: 'real_estate', label: '🏠 נדל"ן' },
  { value: 'cars', label: '🚗 רכב' },
  { value: 'hr', label: '👔 כוח אדם' },
  { value: 'other', label: '📋 אחר' },
]

interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

export const SpotlightTutorial = () => {
  const location = useLocation()
  const {auth, currentUser} = useAuth()

  // "Stuck?" → customer service on WhatsApp, prefilled with who they are and which step
  // lost them. Mirrors the post-register wizard's help button, and is offered on every
  // step for the same reason: someone blocked mid-tutorial can otherwise only abandon it,
  // and we'd never find out where. Declared up here because the connect-profile step
  // renders through renderConnectTooltip(), which runs before the later step locals exist.
  const openTutorialHelp = (stepTitle: string) => {
    const lines = [
      'שלום, אני צריך/ה עזרה במדריך ההדרכה 🙏',
      `שם משתמש: ${(currentUser as any)?.username || 'לקוח'}`,
      `שלב נוכחי: ${stepTitle}`,
    ]
    window.open(`https://wa.me/${TUTORIAL_SUPPORT_WA}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank', 'noopener')
  }
  const renderHelpButton = (stepTitle: string) => (
    <button type="button" className="spotlight-help-btn" onClick={() => openTutorialHelp(stepTitle)}>
      💬 צריכים עזרה? דברו איתנו
    </button>
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const [rawSteps, setRawSteps] = useState<TutorialStep[] | null>(null)
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [waitingForTarget, setWaitingForTarget] = useState(false)
  const [showRepeatPrompt, setShowRepeatPrompt] = useState(false)
  const [showWelcome, setShowWelcome] = useState(true)
  const [selectedOccupation, setSelectedOccupation] = useState<string | null>(null)
  const [savingOccupation, setSavingOccupation] = useState(false)
  const [targetInViewport, setTargetInViewport] = useState(true)
  // Connect profile flow state
  const [connectPhase, setConnectPhase] = useState<'form' | 'iframe' | 'success'>('form')
  const [connectProfile, setConnectProfile] = useState<any>(null)
  // /c/:code login page for connectProfile, streamed inside the phone mockup.
  const [connectEmbedUrl, setConnectEmbedUrl] = useState('')
  const [profileUsername, setProfileUsername] = useState('')
  const [profilePassword, setProfilePassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [connectSaving, setConnectSaving] = useState(false)
  const [showComplete, setShowComplete] = useState(false)
  const [showTourPrompt, setShowTourPrompt] = useState(false)
  const [hoverImage, setHoverImage] = useState<{url: string, caption?: string, top: number, left: number} | null>(null)
  // The banner is position:fixed against the LAYOUT viewport. When the on-screen keyboard
  // opens — which it does on every step where the user types, i.e. most of the post-creation
  // ones — the visual viewport shrinks and scrolls independently, and a top-fixed element
  // ends up off-screen above it. The user is then typing into a modal with no visible
  // instructions and, worse, no "המשך" to move on with.
  // VisualViewport reports how far the visible area has been pushed; offsetting the banner
  // by that much keeps it pinned to the top of what the user can actually see.
  const bannerElRef = useRef<HTMLDivElement | null>(null)
  const syncBannerToViewport = useCallback(() => {
    const node = bannerElRef.current
    const vv = (window as any).visualViewport
    if (!node || !vv) return
    node.style.transform = `translateY(${Math.max(0, Math.round(vv.offsetTop))}px)`
  }, [])
  const bannerRef = useCallback((node: HTMLDivElement | null) => {
    bannerElRef.current = node
    if (node) {
      const h = node.offsetHeight
      document.documentElement.style.setProperty('--spotlight-banner-h', `${h}px`)
      // Re-measure when expanded state changes
      requestAnimationFrame(() => {
        const h2 = node.offsetHeight
        document.documentElement.style.setProperty('--spotlight-banner-h', `${h2}px`)
        syncBannerToViewport()
      })
    } else {
      document.documentElement.style.removeProperty('--spotlight-banner-h')
    }
  }, [currentIndex, syncBannerToViewport]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const vv = (window as any).visualViewport
    if (!vv) return
    // Both events matter: 'resize' is the keyboard appearing/disappearing, 'scroll' is the
    // page being panned while it's open (e.g. focusing a field further down the modal).
    vv.addEventListener('resize', syncBannerToViewport)
    vv.addEventListener('scroll', syncBannerToViewport)
    return () => {
      vv.removeEventListener('resize', syncBannerToViewport)
      vv.removeEventListener('scroll', syncBannerToViewport)
    }
  }, [syncBannerToViewport])
  const profileCheckRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const initialConnectFbIdRef = useRef<string | null>(null)
  const initialConnectHealthRef = useRef<string | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)
  const rafRef = useRef<number>(0)
  const targetElRef = useRef<HTMLElement | null>(null)
  const elevatedEls = useRef<HTMLElement[]>([])
  const goNextRef = useRef<() => void>(() => {})
  const revealedSheetRef = useRef(false) // Track if clickToReveal opened a sheet

  // Parse ?tut= and ?step= params
  const searchParams = new URLSearchParams(location.search)
  const tutParam = searchParams.get('tut')
  const stepParam = searchParams.get('step')

  // Update step in URL without triggering navigation
  const updateStepInUrl = useCallback((stepIndex: number | null) => {
    const params = new URLSearchParams(window.location.search)
    if (stepIndex !== null) {
      params.set('step', String(stepIndex))
    } else {
      params.delete('step')
    }
    const newUrl = location.pathname + '?' + params.toString()
    window.history.replaceState(null, '', newUrl)
  }, [location.pathname])

  // Initialize tutorial steps — restore position from URL
  useEffect(() => {
    if (tutParam && tutorials[tutParam]) {
      const tutSteps = tutorials[tutParam]
      setRawSteps(tutSteps)
      if (stepParam !== null) {
        const idx = Math.min(parseInt(stepParam, 10) || 0, tutSteps.length - 1)
        setCurrentIndex(idx)
        setShowWelcome(false) // Already past welcome
      } else {
        setCurrentIndex(0)
        // The post-onboarding "onboarded" variant already collected occupation in the
        // onboarding wizard, so skip the welcome/occupation screen → start at select-campaign.
        setShowWelcome(tutParam !== 'onboarded')
        setTargetRect(null) // Ensure no click forwarder during welcome
      }
    } else {
      setRawSteps(null)
    }
  }, [tutParam]) // intentionally exclude stepParam to avoid re-init on step changes

  // Track mobile breakpoint
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Admin support sessions can always get out of a client's tutorial.
  const isAdminSession = useMemo(() => {
    try { return !!(getAdminToken && getAdminToken()) } catch (e) { return false }
  }, [])

  // Whether CUSTOMERS get the "skip tutorial" escape hatch. Global admin setting
  // (SystemSetting tutorial_skip_enabled), off by default — the tour is what
  // configures the account, so quitting halfway leaves it half-set-up.
  const [skipSettingEnabled, setSkipSettingEnabled] = useState(false)
  useEffect(() => {
    if (!tutParam) return
    let cancelled = false
    axios
      .get(`${API_URL}/api/account-settings`)
      .then((r) => { if (!cancelled) setSkipSettingEnabled(r.data?.tutorial_skip_enabled === true) })
      .catch(() => { /* stay closed — no skip button if we can't tell */ })
    return () => { cancelled = true }
  }, [tutParam])

  const canSkipTutorial = isAdminSession || skipSettingEnabled

  // The steps actually shown. `desktopOnly` stops are dropped on mobile, so the
  // tour ends at the campaign card there instead of walking through stops that
  // repeat what the bottom sheet already showed.
  const steps = useMemo(() => {
    if (!rawSteps) return null
    return isMobile ? rawSteps.filter((s) => !s.desktopOnly) : rawSteps
  }, [rawSteps, isMobile])

  // Rotating to mobile mid-tour can leave the index past the end of the shorter
  // list — pull it back rather than rendering an undefined step.
  useEffect(() => {
    if (steps && currentIndex > steps.length - 1) setCurrentIndex(Math.max(0, steps.length - 1))
  }, [steps, currentIndex])

  // Publish the active step id on <body> so page CSS can react to it — used to grey
  // out the edit/delete/create controls while the user is only meant to PICK a
  // campaign, without threading tutorial state through the campaign components.
  useEffect(() => {
    const id = steps && !showWelcome ? steps[currentIndex]?.id : null
    if (id) document.body.setAttribute('data-spotlight-step', id)
    else document.body.removeAttribute('data-spotlight-step')
    return () => document.body.removeAttribute('data-spotlight-step')
  }, [steps, currentIndex, showWelcome])

  // Push modal content down when top banner is visible
  const isModalBannerStep = isMobile && !showWelcome && steps?.[currentIndex] && (steps[currentIndex].modalId || steps[currentIndex].customModalSelector || steps[currentIndex].clickToReveal || steps[currentIndex].mobileTopBanner)

  useEffect(() => {
    if (!isModalBannerStep) return

    // Add class to body so CSS can shift modals down
    document.body.classList.add('spotlight-banner-active')
    return () => {
      document.body.classList.remove('spotlight-banner-active')
    }
  }, [isModalBannerStep])

  // Clean up target element styling and any elevated modal containers
  const cleanupTarget = useCallback(() => {
    if (targetElRef.current) {
      targetElRef.current.style.removeProperty('position')
      targetElRef.current.style.removeProperty('z-index')
      targetElRef.current.style.removeProperty('pointer-events')
      targetElRef.current.classList.remove('spotlight-target-active')
      targetElRef.current = null
    }
    // Restore z-index and backdrop-filter on any elevated modal containers/backdrops
    for (const el of elevatedEls.current) {
      el.style.removeProperty('z-index')
      el.style.removeProperty('backdrop-filter')
      el.style.removeProperty('-webkit-backdrop-filter')
    }
    elevatedEls.current = []
  }, [])

  // Dismiss: remove ?tut= param and remember skip
  const dismiss = useCallback(() => {
    cleanupTarget()
    if (revealedSheetRef.current) {
      revealedSheetRef.current = false
      const closeBtn = document.querySelector('.campaign-sheet-close') as HTMLElement | null
      if (closeBtn) closeBtn.click()
    }
    setRawSteps(null)
    setWaitingForTarget(false)
    localStorage.setItem('tutorial_skipped', 'true')
    const params = new URLSearchParams(window.location.search)
    params.delete('tut')
    params.delete('step')
    const newSearch = params.toString()
    const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '')
    window.history.replaceState(null, '', newUrl)
  }, [location, cleanupTarget])

  // Bail out of the tutorial from any step. Same teardown as dismiss(), behind a
  // confirm because the tour is also the account setup — see canSkipTutorial.
  const skipTutorial = useCallback(() => {
    if (window.confirm('האם אתם בטוחים שברצונכם לצאת מהמדריך? תוכלו להריץ אותו שוב מהתפריט בכל רגע.')) {
      dismiss()
    }
  }, [dismiss])

  // Full-width "skip" affordance for the tooltip / bottom-sheet surfaces.
  const renderSkipButton = () => (
    canSkipTutorial ? (
      <button type="button" className="spotlight-skip-btn" onClick={skipTutorial}>
        דלג על המדריך{isAdminSession && !skipSettingEnabled ? ' (אדמין)' : ''}
      </button>
    ) : null
  )

  // Advance to next step (or show completion screen if last)
  const goNext = useCallback(() => {
    if (!steps) return
    cleanupTarget()
    // Close click-to-reveal panel if next step doesn't also use it
    if (revealedSheetRef.current) {
      const nextStep = steps[currentIndex + 1]
      if (!nextStep || !nextStep.clickToReveal) {
        revealedSheetRef.current = false
        const closeBtn = document.querySelector('.campaign-sheet-close') as HTMLElement | null
        if (closeBtn) closeBtn.click()
      }
    }
    if (currentIndex < steps.length - 1) {
      // After connect-profile-iframe, show congrats/tour prompt before dashboard tour
      if (steps[currentIndex].id === 'connect-profile-iframe') {
        setShowTourPrompt(true)
        return
      }
      const nextIndex = currentIndex + 1
      setCurrentIndex(nextIndex)
      updateStepInUrl(nextIndex)
    } else {
      // End of dashboard tour — just dismiss
      if (steps[currentIndex].showSkipTour) {
        dismiss()
      } else {
        setShowComplete(true)
        updateStepInUrl(null)
      }
    }
  }, [steps, currentIndex, cleanupTarget, updateStepInUrl, dismiss])

  // Advance past N steps (e.g. skip the "how to use groups" step when the user is happy
  // with the auto-selected groups). Ends the tutorial if it runs past the last step.
  const goSkip = useCallback((count: number) => {
    if (!steps) return
    cleanupTarget()
    const target = currentIndex + Math.max(1, count || 1)
    if (target <= steps.length - 1) {
      setCurrentIndex(target)
      updateStepInUrl(target)
    } else {
      dismiss()
    }
  }, [steps, currentIndex, cleanupTarget, updateStepInUrl, dismiss])

  // Keep goNextRef in sync so listeners can call it
  useEffect(() => {
    goNextRef.current = goNext
  }, [goNext])

  // Resolve the right target selector: prefer mobileTarget on small screens if the element exists
  const resolveTarget = useCallback((step: TutorialStep): string => {
    if (step.mobileTarget && isMobile) {
      const mobileEl = document.querySelector(step.mobileTarget)
      if (mobileEl) return step.mobileTarget
    }
    return step.target
  }, [isMobile])

  // Find and track target element rect
  const updateRect = useCallback(() => {
    if (!steps || !steps[currentIndex]) return
    if (showWelcome) return
    const selector = resolveTarget(steps[currentIndex])
    const el = document.querySelector(selector) as HTMLElement | null
    if (el) {
      const r = el.getBoundingClientRect()
      const padding = steps[currentIndex].spotlightPadding || 8
      setTargetRect({
        top: r.top - padding,
        left: r.left - padding,
        width: r.width + padding * 2,
        height: r.height + padding * 2,
      })
      // Check if target is within viewport
      setTargetInViewport(r.top >= -50 && r.bottom <= window.innerHeight + 50)
    } else {
      setTargetRect(null)
      setTargetInViewport(false)
    }
  }, [steps, currentIndex, resolveTarget, showWelcome, location.pathname])

  // Main effect: set up current step
  useEffect(() => {
    if (!steps || !steps[currentIndex]) return
    if (showWelcome) return // Don't set up step while welcome screen is showing

    // If campaign creation modal is open and we're past that step, go back to it
    if (currentIndex >= 2 && steps[currentIndex].id !== 'fill-campaign-modal') {
      const campaignModal = document.getElementById('kt_modal_1')
      if (campaignModal?.classList.contains('show')) {
        const fillCampaignIdx = steps.findIndex(s => s.id === 'fill-campaign-modal')
        if (fillCampaignIdx >= 0) {
          setCurrentIndex(fillCampaignIdx)
          updateStepInUrl(fillCampaignIdx)
          return
        }
      }
    }

    if (steps[currentIndex].advanceOn === 'connect-profile' || steps[currentIndex].advanceOn === 'connect-profile-iframe') return // Handled separately

    const step = steps[currentIndex]

    const targetSelector = resolveTarget(step)

    // Helper: check if element is visible (present in DOM and has dimensions)
    const isVisible = (el: HTMLElement | null): boolean => {
      if (!el) return false
      const rect = el.getBoundingClientRect()
      return rect.height > 0 && rect.width > 0
    }

    // A step that only opens the picker in order to satisfy requireCampaignSelected has
    // nothing left to reveal once a campaign IS selected — and the campaign bar
    // deliberately stops reopening the sheet at that point. Without this the reveal
    // click lands on an inert bar, `.campaign-sheet.show` never appears, and the poll
    // below spins forever: the step renders as a permanent loading bar.
    const revealAlreadySatisfied =
      !!step.requireCampaignSelected && /^\/campaigns\/[A-Za-z0-9]/.test(location.pathname)

    // Click-to-reveal: programmatically click an element to reveal the target (e.g. open a bottom sheet)
    if (step.clickToReveal && isMobile && !revealAlreadySatisfied) {
      const revealSelector = step.clickToRevealSelector || targetSelector
      const revealedEl = document.querySelector(revealSelector) as HTMLElement | null
      if (!revealedEl) {
        // Element doesn't exist yet — click to reveal and poll
        const clickEl = document.querySelector(step.clickToReveal) as HTMLElement | null
        if (clickEl) clickEl.click()
        revealedSheetRef.current = true
        setWaitingForTarget(true)
        const interval = setInterval(() => {
          const found = document.querySelector(revealSelector) as HTMLElement | null
          if (found) {
            clearInterval(interval)
            setWaitingForTarget(false)
          }
        }, 200)
        return () => clearInterval(interval)
      }
      // Elevate the revealed panel and its overlay above the spotlight overlay
      const sheetEl = document.querySelector('.campaign-sheet') as HTMLElement | null
      if (sheetEl) {
        sheetEl.style.zIndex = '10012'
        elevatedEls.current.push(sheetEl)
      }
      const sheetOverlay = document.querySelector('.campaign-sheet-overlay') as HTMLElement | null
      if (sheetOverlay) {
        sheetOverlay.style.zIndex = '10011'
        elevatedEls.current.push(sheetOverlay)
      }
    }

    // If step needs to wait for target (or a custom selector) to be visible
    if (step.waitForTarget) {
      const waitSelector = step.waitForSelector || targetSelector
      const waitEl = document.querySelector(waitSelector) as HTMLElement | null
      const targetEl = document.querySelector(targetSelector) as HTMLElement | null
      if (!isVisible(waitEl) || !isVisible(targetEl)) {
        // If target exists in DOM but is hidden (e.g. desktop-only element on mobile), skip the step
        // But don't skip steps with a modalId — those will open their modal and wait
        if (targetEl && !isVisible(targetEl) && !step.modalId) {
          const isHiddenByCSS = targetEl.offsetParent === null && getComputedStyle(targetEl).position !== 'fixed'
          if (isHiddenByCSS) {
            setTimeout(() => goNextRef.current(), 100)
            return
          }
        }

        setWaitingForTarget(true)

        // If step has a modalId, try to open it
        if (step.modalId) {
          const modalEl = document.getElementById(step.modalId)
          if (modalEl) {
            BsModal.getOrCreateInstance(modalEl).show()
          }
        }

        // Poll until both the wait selector and target are visible
        const interval = setInterval(() => {
          // Keep trying to open the modal if needed
          if (step.modalId) {
            const modalEl = document.getElementById(step.modalId)
            if (modalEl) {
              BsModal.getOrCreateInstance(modalEl).show()
            }
          }
          const foundWait = document.querySelector(waitSelector) as HTMLElement | null
          const foundTarget = document.querySelector(targetSelector) as HTMLElement | null
          if (isVisible(foundWait) && isVisible(foundTarget)) {
            clearInterval(interval)
            setWaitingForTarget(false)
          }
        }, 200)
        return () => clearInterval(interval)
      }
    }

    setWaitingForTarget(false)
    const el = document.querySelector(targetSelector) as HTMLElement | null
    if (!el || !isVisible(el)) {
      // If target exists but is hidden by CSS (e.g. desktop-only on mobile), skip the step
      if (el && el.offsetParent === null && getComputedStyle(el).position !== 'fixed') {
        setTimeout(() => goNextRef.current(), 100)
      }
      return
    }

    // Scroll into view — use scrollTarget if specified, otherwise the target element
    const scrollEl = step.scrollTarget
      ? (document.querySelector(step.scrollTarget) as HTMLElement | null) || el
      : el
    const scrollRect = scrollEl.getBoundingClientRect()
    const inViewport = scrollRect.top >= 0 && scrollRect.bottom <= window.innerHeight
    // Picking a campaign changes the URL, which re-runs this effect — re-centering the
    // list at that moment yanks the page out from under the user right as they tap.
    // Once a campaign is chosen on the select step, leave the scroll position alone.
    const campaignAlreadyPicked = step.id === 'select-campaign'
      && /^\/campaigns\/[A-Za-z0-9]/.test(window.location.pathname)
    if (!inViewport && !campaignAlreadyPicked) {
      // Delay scroll to run after React rendering and layout settle
      // (scrollIntoView gets cancelled during React render cycle, so use scrollTo in a timeout)
      setTimeout(() => {
        const freshRect = scrollEl.getBoundingClientRect()
        const targetY = window.scrollY + freshRect.top - (window.innerHeight / 2) + (freshRect.height / 2)
        window.scrollTo(0, targetY)
      }, 350)
    }

    // Elevate target above overlay so user can interact with it
    // Preserve fixed positioning (e.g. campaign sheet) — only set relative if not already fixed
    //
    // Except once the select step is satisfied: on mobile its target is the footer bar,
    // which by then is inert and meant to read as dark like everything else. Spotlighting
    // it would light up and cut a hole around the one thing we've just put out of play,
    // pointing the user back at a decision they've already made. Leave it under the mask
    // and let the banner carry the step.
    if (campaignAlreadyPicked && isMobile) {
      // Explicitly drop any elevation this step had applied before the pick, rather than
      // relying on the previous run's cleanup having fired — leaving the bar at
      // z-index 10012 would keep it lit above the mask, which is the whole problem.
      cleanupTarget()
      setTargetRect(null)
      return
    }
    const currentPosition = getComputedStyle(el).position
    if (currentPosition !== 'fixed') {
      el.style.position = 'relative'
    }
    el.style.zIndex = '10012'
    el.style.pointerEvents = 'auto'
    el.classList.add('spotlight-target-active')
    targetElRef.current = el

    // For modal steps: elevate the entire modal container and its backdrop
    // so they sit above the SVG overlay (z-index 10010)
    let preventCloseHandler: ((e: Event) => void) | null = null
    let backdropObserver: MutationObserver | null = null
    const hiddenButtons: {el: HTMLElement, prev: string}[] = []

    // Elevate modal above overlay if step has a modalId (regardless of advanceOn type)
    if (step.modalId) {
      const modalRoot = document.getElementById(step.modalId)
      if (modalRoot) {
        modalRoot.style.zIndex = '10012'
        elevatedEls.current.push(modalRoot)

        // Prevent closing via backdrop/escape (and optionally X button)
        if (step.preventClose) {
          let dismissClicked = false
          // Use event delegation on the modal root to catch clicks on dynamically rendered buttons
          const onModalClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            const clickedButton = target.closest('button')
            if (!clickedButton) return
            if (step.preventClose === 'backdrop-only') {
              // Any button click allows close
              dismissClicked = true
            } else {
              // Only submit buttons or dismiss buttons allow close
              if (clickedButton.getAttribute('type') === 'submit' || clickedButton.getAttribute('data-bs-dismiss') === 'modal') {
                dismissClicked = true
              }
            }
          }
          modalRoot.addEventListener('click', onModalClick, true)

          preventCloseHandler = (e: Event) => {
            if (dismissClicked) {
              dismissClicked = false
              return
            }
            e.preventDefault()
          }
          modalRoot.addEventListener('hide.bs.modal', preventCloseHandler)

          ;(preventCloseHandler as any)._onModalClick = onModalClick
        }
      }
      // Bootstrap creates a .modal-backdrop sibling — elevate it and disable blur.
      const disableBackdropBlur = () => {
        const backdrop = document.querySelector('.modal-backdrop') as HTMLElement
        if (backdrop) {
          backdrop.style.zIndex = '10011'
          backdrop.style.setProperty('backdrop-filter', 'none')
          backdrop.style.setProperty('-webkit-backdrop-filter', 'none')
          elevatedEls.current.push(backdrop)
          return true
        }
        return false
      }
      if (!disableBackdropBlur()) {
        backdropObserver = new MutationObserver(() => {
          if (disableBackdropBlur()) {
            backdropObserver?.disconnect()
            backdropObserver = null
          }
        })
        backdropObserver.observe(document.body, {childList: true, subtree: true})
      }
    }

    if (step.advanceOn === 'modal-close' || step.advanceOn === 'modal-close-repeat') {
      if (step.customModalSelector) {
        // Custom modal: elevate the backdrop wrapper
        const customBackdrop = document.querySelector(step.customModalSelector) as HTMLElement
        if (customBackdrop) {
          customBackdrop.style.zIndex = '10012'
          customBackdrop.style.setProperty('backdrop-filter', 'none')
          customBackdrop.style.setProperty('-webkit-backdrop-filter', 'none')
          elevatedEls.current.push(customBackdrop)

          // Prevent closing by clicking backdrop
          if (step.preventClose) {
            const blockBackdropClick = (e: MouseEvent) => {
              if (e.target === customBackdrop) {
                e.stopPropagation()
                e.preventDefault()
              }
            }
            customBackdrop.addEventListener('click', blockBackdropClick, true)
            elevatedEls.current.push(customBackdrop) // for cleanup tracking
          }
        }
      }
    }

    // Hide close/cancel/save buttons (applies to any step type)
    if (step.hideCloseButtons) {
      document.querySelectorAll(step.hideCloseButtons).forEach((btn) => {
        const htmlBtn = btn as HTMLElement
        hiddenButtons.push({el: htmlBtn, prev: htmlBtn.style.display})
        htmlBtn.style.display = 'none'
      })
    }

    // Measure after scroll settles
    setTimeout(updateRect, 400)
    updateRect()

    // Track scroll/resize
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(updateRect)
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)

    observerRef.current = new ResizeObserver(updateRect)
    observerRef.current.observe(el)

    // --- Advance logic ---
    let cleanupAdvance: (() => void) | null = null

    if (step.advanceOn === 'click') {
      // Advance when user clicks the target element
      const onClick = () => {
        setTimeout(() => goNextRef.current(), 150)
      }
      el.addEventListener('click', onClick, {once: true})
      cleanupAdvance = () => el.removeEventListener('click', onClick)
    } else if (step.advanceOn === 'modal-close' || step.advanceOn === 'modal-close-repeat') {
      const onModalClosed = () => {
        if (step.advanceOn === 'modal-close-repeat') {
          // Show repeat prompt instead of advancing
          setShowRepeatPrompt(true)
        } else {
          setTimeout(() => goNextRef.current(), 200)
        }
      }
      // For Bootstrap modals: listen for 'hidden.bs.modal'
      if (step.modalId) {
        const modalEl = document.getElementById(step.modalId)
        if (modalEl) {
          const onHidden = () => onModalClosed()
          modalEl.addEventListener('hidden.bs.modal', onHidden, {once: true})
          cleanupAdvance = () => modalEl.removeEventListener('hidden.bs.modal', onHidden)
        }
      }
      // For custom modals: watch for removal from DOM
      if (step.customModalSelector) {
        const observer = new MutationObserver(() => {
          const stillExists = document.querySelector(step.customModalSelector!)
          if (!stillExists) {
            observer.disconnect()
            onModalClosed()
          }
        })
        observer.observe(document.body, {childList: true, subtree: true})
        cleanupAdvance = () => observer.disconnect()
      }
    }

    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      cancelAnimationFrame(rafRef.current)
      observerRef.current?.disconnect()
      cleanupAdvance?.()
      // Restore hidden buttons
      for (const {el, prev} of hiddenButtons) {
        el.style.display = prev
      }
      // Remove prevent-close handler and delegated click listener
      if (preventCloseHandler && step.modalId) {
        const modalRoot = document.getElementById(step.modalId)
        modalRoot?.removeEventListener('hide.bs.modal', preventCloseHandler)
        const onModalClick = (preventCloseHandler as any)._onModalClick as ((e: MouseEvent) => void) | null
        if (onModalClick) {
          modalRoot?.removeEventListener('click', onModalClick, true)
        }
      }
      // Disconnect backdrop watcher if still observing
      backdropObserver?.disconnect()
    }
    // location.pathname: picking a campaign navigates without changing the step index,
    // so without it this effect never re-runs and the footer stays elevated and spotlit
    // after the choice has been made.
  }, [steps, currentIndex, updateRect, waitingForTarget, resolveTarget, showWelcome, isMobile, location.pathname])

  // Connect profile: load profile data when reaching this step
  useEffect(() => {
    if (!steps || !steps[currentIndex]) return
    const advanceType = steps[currentIndex].advanceOn
    if (advanceType !== 'connect-profile' && advanceType !== 'connect-profile-iframe') return

    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/get-single-profile`, {
          params: { api_token: auth?.api_token }
        })
        const profileData = response.data?.bot || response.data
        const allUsers = profileData?.all_users || []
        const loggedInParent = allUsers.find((u: any) => u.facebook_id && !u.parent_user
          && u.last_health_check && new Date(u.last_health_check).getTime() > Date.now() - 5 * 60 * 1000)

        if (loggedInParent) {
          // Actually connected (recent health check) — skip all connect-profile steps
          setConnectProfile(loggedInParent)
          setTimeout(() => {
            // Skip past both connect-profile and connect-profile-iframe steps
            if (!steps) return
            let nextIdx = currentIndex + 1
            while (nextIdx < steps.length && (steps[nextIdx].advanceOn === 'connect-profile' || steps[nextIdx].advanceOn === 'connect-profile-iframe')) {
              nextIdx++
            }
            if (nextIdx < steps.length) {
              setCurrentIndex(nextIdx)
              updateStepInUrl(nextIdx)
            } else {
              // No more steps — show tour prompt or complete
              setShowTourPrompt(true)
            }
          }, 200)
          return
        } else {
          const parentProfile = allUsers.find((u: any) => !u.parent_user) || profileData
          initialConnectFbIdRef.current = parentProfile.facebook_id || null
          initialConnectHealthRef.current = parentProfile.last_health_check || null
          setConnectProfile(parentProfile)
          // If we're on the iframe step, go directly to iframe phase
          if (advanceType === 'connect-profile-iframe') {
            setConnectPhase('iframe')
          } else {
            setConnectPhase('form')
          }
        }
      } catch (e) {
        console.error('Error loading profile:', e)
      }
    }

    loadProfile()

    return () => {
      if (profileCheckRef.current) {
        clearInterval(profileCheckRef.current)
        profileCheckRef.current = null
      }
    }
  }, [steps, currentIndex, auth?.api_token])

  // Connect profile: poll for login when iframe is showing
  useEffect(() => {
    if (connectPhase !== 'iframe' || !connectProfile) return

    const checkLogin = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/get-single-profile`, {
          params: { api_token: auth?.api_token }
        })
        const profileData = response.data?.bot || response.data
        const allUsers = profileData?.all_users || []
        const targetProfile = allUsers.find((u: any) => u._id === connectProfile._id)

        if (targetProfile && targetProfile.facebook_id) {
          const isNewFacebookId = targetProfile.facebook_id !== initialConnectFbIdRef.current
          const healthCheckChanged = targetProfile.last_health_check && targetProfile.last_health_check !== initialConnectHealthRef.current
          if (isNewFacebookId || healthCheckChanged) {
            console.log('[Tutorial] Profile logged in!', targetProfile)
            setConnectProfile(targetProfile)
            if (profileCheckRef.current) {
              clearInterval(profileCheckRef.current)
              profileCheckRef.current = null
            }
            goNextRef.current()
          }
        }
      } catch (e) {
        console.error('Error checking profile login:', e)
      }
    }

    profileCheckRef.current = setInterval(checkLogin, 10000)
    return () => {
      if (profileCheckRef.current) {
        clearInterval(profileCheckRef.current)
        profileCheckRef.current = null
      }
    }
  }, [connectPhase, connectProfile, auth?.api_token])

  // The login iframe used to point at profilelogin.fbzipper.com/?id=<profile>.
  // That host is not the login surface any more — /c/:code is, minted per
  // profile — so resolve a link whenever this step is on screen. Reused rather
  // than re-minted server-side, so re-entering the step is free.
  const advanceOnNow = steps?.[currentIndex]?.advanceOn
  useEffect(() => {
    const onIframeStep = advanceOnNow === 'connect-profile-iframe' || connectPhase === 'iframe'
    if (!onIframeStep || !connectProfile?._id) return
    let cancelled = false
    axios
      .post(`${API_URL}/api/profiles/${connectProfile._id}/connect-link`, {})
      .then((r) => { if (!cancelled && r.data?.url) setConnectEmbedUrl(r.data.url) })
      .catch((e) => console.error('[Tutorial] connect link failed:', e))
    return () => { cancelled = true }
  }, [advanceOnNow, connectPhase, connectProfile?._id])

  if (!steps || !tutParam) return null

  // Welcome screen with occupation selection (skipped for the post-onboarding variant).
  if (showWelcome && tutParam !== 'onboarded') {
    const handleStart = async () => {
      if (!selectedOccupation) return
      setSavingOccupation(true)
      try {
        await axios.post(`${API_URL}/api/library-occupation`, { occupation: selectedOccupation })
      } catch (e) {
        console.error('Error saving occupation:', e)
      } finally {
        setSavingOccupation(false)
        setShowWelcome(false)
        updateStepInUrl(0)
      }
    }

    return createPortal(
      <div className="spotlight-tutorial">
        <svg className="spotlight-overlay-svg">
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" />
        </svg>
        <div className="spotlight-welcome">
          <div className="spotlight-welcome-icon">🚀</div>
          <h2 className="spotlight-welcome-title">ברוכים הבאים ל־FB Zipper</h2>
          <p className="spotlight-welcome-subtitle">
            המערכת המתקדמת בעולם לפרסום בקבוצות פייסבוק.<br />
            תוך מספר דקות הפוסטים יתחילו להתפרסם <strong>אוטומטית</strong> – בלי ידע טכני ובלי לדבר עם אף אחד.
          </p>

          <video
            className="spotlight-welcome-video"
            src="https://zipper.ams3.cdn.digitaloceanspaces.com/tutorial/onboarding/welcome.mp4"
            controls
            autoPlay
            muted
            playsInline
          />

          <div className="spotlight-welcome-occupation">
            <p className="spotlight-welcome-occupation-label">בחרו קטגוריה ולחצו על המשך</p>
            <div className="spotlight-welcome-occupation-options">
              {occupationOptions.map(opt => (
                <button
                  key={opt.value}
                  className={`spotlight-welcome-occupation-btn${selectedOccupation === opt.value ? ' selected' : ''}`}
                  onClick={() => setSelectedOccupation(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <button
            className="spotlight-welcome-btn"
            disabled={!selectedOccupation || savingOccupation}
            onClick={handleStart}
          >
            {savingOccupation ? 'שומר...' : 'המשך'}
          </button>
          {canSkipTutorial && (
            <button type="button" className="spotlight-skip-link" onClick={skipTutorial}>
              דלג על המדריך
            </button>
          )}
        </div>
      </div>,
      document.body
    )
  }

  // Completion/summary screen — shown after the last step
  if (showComplete) {
    return createPortal(
      <div className="spotlight-tutorial">
        <svg className="spotlight-overlay-svg">
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" />
        </svg>
        <div className="spotlight-connect">
          <div className="wizard-step complete-step">
            <div className="step-icon">🎉</div>
            <h2>כל הכבוד!</h2>
            <p>
              ההגדרה הושלמה בהצלחה!<br />
              המערכת תתחיל לפרסם את הפוסטים שלך באופן אוטומטי.
            </p>

            {connectProfile && connectProfile.facebook_id && (
              <div className="connected-profile-card">
                <div className="profile-avatar-wrapper">
                  <img
                    src={`https://graph.facebook.com/${connectProfile.facebook_id}/picture?type=large&access_token=${FB_ACCESS_TOKEN}`}
                    alt="Profile"
                    className="profile-pic"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/media/avatars/blank.png'
                    }}
                  />
                  <div className="connected-badge">✓</div>
                </div>
                <div className="profile-details">
                  <span className="profile-name">
                    {connectProfile.first_name || connectProfile.name || ''} {connectProfile.last_name || ''}
                  </span>
                  <span className="profile-status">מחובר ומוכן לפרסום</span>
                </div>
              </div>
            )}

            <p className="dashboard-hint">
              💡 תמיד תוכל לערוך את הגדרות הקמפיין ולצפות בסטטיסטיקות ובנתונים – הכול מדף הבית.
            </p>

            <button className="spotlight-welcome-btn" style={{marginTop: '20px', width: '100%'}} onClick={dismiss}>
              סיום ומעבר לדף הבית
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // Tour prompt: congrats screen with "start tour" button — shown after connect-profile-iframe
  if (showTourPrompt) {
    const startTour = () => {
      setShowTourPrompt(false)
      const nextIndex = currentIndex + 1
      setCurrentIndex(nextIndex)
      updateStepInUrl(nextIndex)
    }

    return createPortal(
      <div className="spotlight-tutorial">
        <svg className="spotlight-overlay-svg">
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" />
        </svg>
        <div className="spotlight-connect">
          <div className="wizard-step complete-step">
            <div className="step-icon">🎉</div>
            <h2>כל הכבוד!</h2>
            <p>
              ההגדרה הושלמה בהצלחה!<br />
              המערכת תתחיל לפרסם את הפוסטים שלך באופן אוטומטי.
            </p>

            {connectProfile && connectProfile.facebook_id && (
              <div className="connected-profile-card">
                <div className="profile-avatar-wrapper">
                  <img
                    src={`https://graph.facebook.com/${connectProfile.facebook_id}/picture?type=large&access_token=${FB_ACCESS_TOKEN}`}
                    alt="Profile"
                    className="profile-pic"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/media/avatars/blank.png'
                    }}
                  />
                  <div className="connected-badge">✓</div>
                </div>
                <div className="profile-details">
                  <span className="profile-name">
                    {connectProfile.first_name || connectProfile.name || ''} {connectProfile.last_name || ''}
                  </span>
                  <span className="profile-status">מחובר ומוכן לפרסום</span>
                </div>
              </div>
            )}

            <p className="dashboard-hint">
              💡 בואו נכיר את לוח הבקרה – מדריך קצר שיעזור לכם להתמצא במערכת.
            </p>

            <button className="spotlight-welcome-btn" style={{marginTop: '20px', width: '100%'}} onClick={startTour}>
              התחל מדריך →
            </button>
            {canSkipTutorial && (
              <button type="button" className="spotlight-skip-link" onClick={skipTutorial}>
                דלג על המדריך
              </button>
            )}
          </div>
        </div>
      </div>,
      document.body
    )
  }

  // Repeat prompt: user can add another post or continue
  if (showRepeatPrompt) {
    return createPortal(
      <div className="spotlight-tutorial">
        <svg className="spotlight-overlay-svg">
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" />
        </svg>
        <div className="spotlight-repeat-prompt">
          <h3>הפוסט נוצר בהצלחה!</h3>
          <p>יש לך כרגע {document.body.getAttribute('data-post-count') || '0'} פוסטים בקמפיין. תוכל להוסיף עוד פוסטים, או להמשיך לשלב הבא.</p>
          <div className="spotlight-repeat-buttons">
            <button
              className="spotlight-repeat-btn add-more"
              onClick={() => {
                setShowRepeatPrompt(false)
                // Go back to the "click add post" step (one step before current)
                cleanupTarget()
                const prevIndex = currentIndex - 1
                setCurrentIndex(prevIndex)
                updateStepInUrl(prevIndex)
              }}
            >
              + הוסף עוד פוסט
            </button>
            <button
              className="spotlight-repeat-btn done"
              onClick={() => {
                setShowRepeatPrompt(false)
                goNext()
              }}
            >
              סיימתי, המשך →
            </button>
          </div>
          {canSkipTutorial && (
            <button type="button" className="spotlight-skip-link" onClick={skipTutorial}>
              דלג על המדריך
            </button>
          )}
        </div>
      </div>,
      document.body
    )
  }

  // While waiting for target (e.g., modal opening), still show the overlay
  if (waitingForTarget) {
    return createPortal(
      <div className="spotlight-tutorial">
        <svg className="spotlight-overlay-svg">
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" />
        </svg>
        <div className="spotlight-loading">
          <div className="spotlight-spinner" />
        </div>
      </div>,
      document.body
    )
  }

  const step = steps[currentIndex]

  // Render description text with "quoted" words bolded, and hover images for matching quotes
  const renderDescription = (text: string, hoverImages?: Record<string, string | {url: string, caption: string}>) => {
    const parts = text.split(/(".*?")/g)
    return parts.map((part, i) => {
      if (part.startsWith('"') && part.endsWith('"')) {
        const imageEntry = hoverImages?.[part]
        if (imageEntry) {
          const url = typeof imageEntry === 'string' ? imageEntry : imageEntry.url
          const caption = typeof imageEntry === 'string' ? undefined : imageEntry.caption
          return (
            <span key={i}>
              <strong>{part}</strong>
              {' '}
              <span
                className="spotlight-hover-image-trigger"
                onMouseEnter={(e) => {
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                  setHoverImage({ url, caption, top: rect.top, left: rect.left + rect.width / 2 })
                }}
                onMouseLeave={() => setHoverImage(null)}
              >
                📷 ראה דוגמה
              </span>
            </span>
          )
        }
        return <strong key={i}>{part}</strong>
      }
      return part
    })
  }

  // Hover image popup — rendered as a separate portal so it's never clipped
  const hoverImagePortal = hoverImage ? createPortal(
    <div
      className="spotlight-hover-image-popup"
      style={{
        position: 'fixed',
        bottom: `${window.innerHeight - hoverImage.top + 8}px`,
        left: `${hoverImage.left}px`,
        transform: 'translateX(-50%)',
        zIndex: 10020,
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        padding: '6px',
        pointerEvents: 'none',
      }}
    >
      <img
        src={hoverImage.url}
        alt=""
        style={{display: 'block', maxWidth: '560px', maxHeight: '800px', borderRadius: '8px', objectFit: 'contain'}}
      />
      {hoverImage.caption && (
        <p style={{margin: '6px 10px 4px', fontSize: '0.8rem', color: '#666', textAlign: 'center', direction: 'rtl', whiteSpace: 'pre-line', lineHeight: '1.5'}}>
          {hoverImage.caption}
        </p>
      )}
    </div>,
    document.body
  ) : null

  // Connect profile step: render self-contained flow (same as OnboardingWizard)
  // Shared connect-profile rendering helper
  const renderConnectTooltip = (stepData: TutorialStep, stepIndex: number) => {
    const label = steps.length > 1 ? `שלב ${stepIndex + 1} מתוך ${steps.length}` : ''
    return (
      <>
        {/* Floating tooltip bubble — same style as other steps */}
        {!isMobile && (
          <div className="spotlight-tooltip spotlight-connect-tooltip" key={stepIndex}>
            <div className={`spotlight-tooltip-arrow arrow-left`} />
            <div className="spotlight-tooltip-content">
              <h3 className="spotlight-tooltip-title">{stepData.title}</h3>
              {stepData.description && (
                <p className="spotlight-tooltip-description" style={{whiteSpace: 'pre-line'}}>{renderDescription(stepData.description, stepData.hoverImages)}</p>
              )}
              {renderHelpButton(stepData.title)}
              {renderSkipButton()}
              {steps.length > 1 && steps.length <= 5 && (
                <div className="spotlight-tooltip-footer">
                  <div className="spotlight-tooltip-dots">
                    {steps.map((_, i) => (
                      <div key={i} className={`dot${i === stepIndex ? ' active' : ''}`} />
                    ))}
                  </div>
                  <span className="spotlight-tooltip-step-label">{label}</span>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Mobile: bottom-sheet */}
        {isMobile && (
          <div className="spotlight-bottom-sheet" key={stepIndex}>
            <div className="spotlight-bottom-sheet-handle" />
            <div className="spotlight-bottom-sheet-content">
              <h3 className="spotlight-bottom-sheet-title">{stepData.title}</h3>
              {stepData.description && (
                <p className="spotlight-bottom-sheet-description" style={{whiteSpace: 'pre-line'}}>{renderDescription(stepData.description, stepData.hoverImages)}</p>
              )}
              {renderHelpButton(stepData.title)}
              {renderSkipButton()}
              {steps.length > 1 && steps.length <= 5 && (
                <div className="spotlight-bottom-sheet-footer">
                  <div className="spotlight-bottom-sheet-dots">
                    {steps.map((_, i) => (
                      <div key={i} className={`dot${i === stepIndex ? ' active' : ''}`} />
                    ))}
                  </div>
                  <span className="spotlight-bottom-sheet-step-label">{label}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    )
  }

  // Connect profile step: form only
  if (step.advanceOn === 'connect-profile') {
    const handleSubmitCredentials = async () => {
      if (!profileUsername || !profilePassword || !connectProfile) return
      setConnectSaving(true)
      try {
        await axios.post(`${API_URL}/api/set-single-profile`, {
          api_token: auth?.api_token,
          _id: connectProfile._id,
          newUsername: profileUsername,
          confirmPassword: profilePassword,
        })
        setConnectPhase('iframe')
        goNext() // Advance to iframe step
      } catch (e) {
        console.error('Error setting profile:', e)
        alert('שגיאה בשמירת הפרטים')
      } finally {
        setConnectSaving(false)
      }
    }

    const handleForgotPassword = async () => {
      if (!connectProfile) return
      setConnectSaving(true)
      try {
        await axios.post(`${API_URL}/api/set-single-profile`, {
          api_token: auth?.api_token,
          _id: connectProfile._id,
          newUsername: '',
          confirmPassword: '',
        })
        setConnectPhase('iframe')
        goNext() // Advance to iframe step
      } catch (e) {
        console.error('Error opening login:', e)
      } finally {
        setConnectSaving(false)
      }
    }

    return createPortal(
      <div className="spotlight-tutorial">
        <svg className="spotlight-overlay-svg">
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" />
        </svg>
        <div className="spotlight-connect">
          <div className="wizard-step profile-step">
            <div className="congrats-banner">
              <div className="congrats-icon">🎉</div>
              <h3>כל הכבוד! כמעט סיימת!</h3>
              <p>רק צריך לחבר את הפרופיל שלך ותוך פחות מדקה הפוסטים שלך יתחילו להתפרסם!</p>
            </div>
            <div className="facebook-login-card">
              <div className="fb-logo">
                <svg viewBox="0 0 36 36" fill="url(#spotlightFbGrad)" height="40" width="40">
                  <defs>
                    <linearGradient id="spotlightFbGrad" x1="50%" x2="50%" y1="97.078%" y2="0%">
                      <stop offset="0%" stopColor="#0062E0"/>
                      <stop offset="100%" stopColor="#19AFFF"/>
                    </linearGradient>
                  </defs>
                  <path d="M15 35.8C6.5 34.3 0 26.9 0 18 0 8.1 8.1 0 18 0s18 8.1 18 18c0 8.9-6.5 16.3-15 17.8l-1-.8h-4l-1 .8z"/>
                  <path fill="#fff" d="M25 23l.8-5H21v-3.5c0-1.4.5-2.5 2.7-2.5H26V7.4c-1.3-.2-2.7-.4-4-.4-4.1 0-7 2.5-7 7v4h-4.5v5H15v12.7c1 .2 2 .3 3 .3s2-.1 3-.3V23h4z"/>
                </svg>
              </div>
              <h2 className="fb-title">התחבר לפייסבוק</h2>
              <div className="fb-form">
                <input type="text" value={profileUsername} onChange={(e) => setProfileUsername(e.target.value)} placeholder="אימייל או מספר טלפון" className="fb-input" autoComplete="off" data-lpignore="true" data-form-type="other" />
                <div className="password-input-wrapper">
                  <input type={showPassword ? 'text' : 'password'} value={profilePassword} onChange={(e) => setProfilePassword(e.target.value)} placeholder="סיסמה" className="fb-input" autoComplete="new-password" data-lpignore="true" data-form-type="other" />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
              </div>
              <button type="button" className="wizard-btn primary" style={{width: '100%', marginTop: '12px'}} disabled={(!profileUsername || !profilePassword) || connectSaving} onClick={handleSubmitCredentials}>
                {connectSaving ? 'מתחבר...' : 'התחבר'}
              </button>
              <button type="button" className="forgot-password-btn" onClick={handleForgotPassword} disabled={connectSaving}>שכחתי סיסמה</button>
              {/* Fallback for everyone: when the normal credential submit won't go
                  through, open the live mobile-browser embed and log in there. Same
                  persistent bot session, so it carries over. Used to be admin-only,
                  which left stuck customers with no way in — and onboarding is where
                  a stuck customer is most likely to appear. */}
              {connectProfile?._id && (
                <div style={{marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f3f5', textAlign: 'center'}}>
                  <button type="button"
                    onClick={async () => {
                      try {
                        const {data} = await axios.post(`${API_URL}/api/profiles/${connectProfile._id}/connect-link`, {})
                        if (data?.url) window.open(data.url, '_blank')
                        else alert('שגיאה ביצירת קישור התחברות')
                      } catch (e) { alert('שגיאה ביצירת קישור התחברות') }
                    }}
                    style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px 16px', background: '#1877F2', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer'}}>
                    🔑 התחבר עם שם משתמש וסיסמה
                  </button>
                  <div style={{fontSize: 11.5, color: '#9ca3af', marginTop: 6}}>לא מצליחים להתחבר? התחברו ידנית עם פרטי הפרופיל</div>
                </div>
              )}
            </div>
          </div>
        </div>
        {renderConnectTooltip(step, currentIndex)}
      </div>,
      document.body
    )
  }

  // Connect profile step: iframe view
  if (step.advanceOn === 'connect-profile-iframe') {
    // Show iframe login view — the per-profile /c/:code page (see the effect above).
    const iframeSrc = connectEmbedUrl

    return <>{createPortal(
      <div className="spotlight-tutorial">
        <svg className="spotlight-overlay-svg">
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" />
        </svg>
        <div className="spotlight-connect">
          <div className="wizard-step iframe-step">
            <h2 className="iframe-title">התחברו לפייסבוק</h2>
            <p className="iframe-subtitle">התחברו לחשבון הפייסבוק שלכם בחלון למטה</p>
            <div className="iphone-mockup">
              <div className="iphone-notch"></div>
              <div className="iphone-screen">
                {iframeSrc ? (
                  <iframe
                    src={iframeSrc}
                    title="Facebook Login"
                  />
                ) : (
                  <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 10, color: '#65676b', fontSize: 13}}>
                    <div className="spotlight-spinner" />
                    מכינים את מסך ההתחברות…
                  </div>
                )}
              </div>
              <div className="iphone-home-bar"></div>
            </div>
            <div className="iframe-loading-hint">
              <div className="spotlight-spinner" style={{width: '16px', height: '16px', borderWidth: '2px'}} />
              <span>ממתין לזיהוי התחברות...</span>
            </div>
            {/* The mockup is deliberately phone-sized, which is tight for typing a
                password. Same login, full screen, in its own tab — the tutorial
                keeps polling here and advances the moment the profile connects. */}
            {iframeSrc && (
              <a
                href={iframeSrc}
                target="_blank"
                rel="noreferrer"
                style={{display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 13, color: '#2b70fa', fontWeight: 600, textDecoration: 'none'}}
              >
                פתח בכרטיסייה חדשה ↗
              </a>
            )}
          </div>
        </div>
        {renderConnectTooltip(step, currentIndex)}
      </div>,
      document.body
    )}{hoverImagePortal}</>
  }

  const borderRadius = step.spotlightBorderRadius || 8

  // "Next" gating: some steps require a campaign to be selected first (URL = /campaigns/:id).
  const mustPickCampaign = !!step.requireCampaignSelected && !/^\/campaigns\/[A-Za-z0-9]/.test(location.pathname)

  // Select-campaign step: once a campaign is chosen, swap the copy to a celebratory
  // "great, you picked X — now hit continue to create a post" message.
  const selectedCampaignName = (() => {
    const m = location.pathname.match(/^\/campaigns\/([^/?]+)/)
    if (!m) return null
    const c = ((currentUser as any)?.campaigns || []).find((x: any) => String(x._id) === m[1])
    return c?.name || null
  })()
  const justPickedCampaign = step.id === 'select-campaign' && !!selectedCampaignName
  const stepTitle = justPickedCampaign ? 'מעולה! 🎉' : step.title
  // Mobile copy wins where a step defines it; {campaign} resolves to the campaign
  // currently open, so a step can name it instead of saying "the campaign".
  const baseDescription = (isMobile && step.mobileDescription) ? step.mobileDescription : step.description
  const stepDescription = justPickedCampaign
    ? `בחרתם בקמפיין "${selectedCampaignName}".\nעכשיו לחצו "המשך →" כדי ליצור את הפוסט הראשון שלכם ✍️`
    : (baseDescription || '').replace(/\{campaign\}/g, selectedCampaignName || 'שלכם')
  const secondaryBtn = step.secondaryButton

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect) return {top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}

    const pos = (isMobile && step.mobileTooltipPosition) ? step.mobileTooltipPosition : (step.tooltipPosition || 'bottom')
    const gap = 16
    const tooltipWidth = 380

    // For next-button steps: keep tooltip visible when target scrolls off-screen.
    // A tall target (e.g. a campaign list longer than the window) fails the
    // in-viewport test even though its top edge is right there on screen, so honour
    // the side the step asked for instead of always dropping to the bottom — which
    // would bury a "pick one of these" prompt below the thing it's pointing at.
    if (step.advanceOn === 'next-button' && !targetInViewport) {
      return {
        ...(pos === 'top' ? {top: 32} : {bottom: 32}),
        left: '50%',
        transform: 'translateX(-50%)',
      }
    }

    switch (pos) {
      case 'top': {
        const leftT = Math.max(16, Math.min(
          targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
          window.innerWidth - tooltipWidth - 16
        ))
        const spaceAbove = targetRect.top - gap
        // Targets that start high on the page leave no real headroom. Sitting the
        // tooltip over the top of the target reads better than squeezing the copy
        // into a scrolling sliver, so pin it near the top and let it overlap.
        if (spaceAbove < 240) return {top: 16, left: leftT}
        return {
          bottom: window.innerHeight - targetRect.top + gap,
          left: leftT,
          maxHeight: spaceAbove - 16,
        }
      }
      case 'bottom':
        return {
          top: targetRect.top + targetRect.height + gap,
          left: Math.max(16, Math.min(
            targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
            window.innerWidth - tooltipWidth - 16
          )),
        }
      case 'left': {
        const topL = Math.max(16, targetRect.top + targetRect.height / 2 - 80)
        return {
          top: topL,
          right: window.innerWidth - targetRect.left + gap,
          maxHeight: `${window.innerHeight - topL - 16}px`,
          overflowY: 'auto' as const,
        }
      }
      case 'right': {
        const topR = Math.max(16, targetRect.top + targetRect.height / 2 - 80)
        return {
          top: topR,
          left: targetRect.left + targetRect.width + gap,
          maxHeight: `${window.innerHeight - topR - 16}px`,
          overflowY: 'auto' as const,
        }
      }
      default:
        return {
          top: targetRect.top + targetRect.height + gap,
          left: targetRect.left,
        }
    }
  }

  const resolvedPosition = (isMobile && step.mobileTooltipPosition) ? step.mobileTooltipPosition : (step.tooltipPosition || 'bottom')
  const arrowClass = resolvedPosition === 'top' ? 'arrow-top'
    : resolvedPosition === 'left' ? 'arrow-left'
    : resolvedPosition === 'right' ? 'arrow-right'
    : 'arrow-bottom'

  const renderMedia = (videoClass: string, audioClass: string) => (
    <>
      {step.videoUrl && (
        <video
          className={videoClass}
          src={step.videoUrl}
          controls
          autoPlay
          muted
          playsInline
        />
      )}
      {step.audioUrl && (
        <audio className={audioClass} src={step.audioUrl} controls />
      )}
    </>
  )

  const renderDots = (className: string) => (
    <div className={className}>
      {steps.map((_, i) => (
        <div key={i} className={`dot${i === currentIndex ? ' active' : ''}`} />
      ))}
    </div>
  )

  const stepLabel = steps.length > 1 ? `שלב ${currentIndex + 1} מתוך ${steps.length}` : ''

  const overlay = (
    <div className="spotlight-tutorial">
      {/* SVG overlay with cutout — blocks clicks on everything except the target */}
      <svg className="spotlight-overlay-svg">
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx={borderRadius}
                ry={borderRadius}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Click-forwarder: a transparent div over the target that forwards clicks to the actual element.
          Needed because z-index elevation on the target doesn't escape parent stacking contexts. */}
      {targetRect && step.advanceOn === 'next-button' && !step.allowInteraction && (
        <div
          style={{
            position: 'fixed',
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            borderRadius: borderRadius,
            zIndex: 10013,
            pointerEvents: 'auto',
          }}
        />
      )}

      {targetRect && step.advanceOn === 'click' && (
        <div
          className="spotlight-click-forwarder"
          style={{
            position: 'fixed',
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            borderRadius: borderRadius,
            zIndex: 10013,
            cursor: 'pointer',
            pointerEvents: 'auto',
          }}
          onClick={() => {
            // Click the actual target element
            const selector = resolveTarget(step)
            const el = document.querySelector(selector) as HTMLElement | null
            el?.click()
          }}
        >
          {/* Red bouncing arrow — comes from opposite side of tooltip */}
          <div className={`spotlight-arrow-container spotlight-arrow-from-${
            resolvedPosition === 'top' ? 'bottom'
            : resolvedPosition === 'bottom' ? 'top'
            : resolvedPosition === 'left' ? 'right'
            : 'left'
          }`}>
            <svg className="spotlight-red-arrow" width="32" height="48" viewBox="0 0 32 48" fill="none">
              <path d="M16 0L16 36" stroke="#ff3333" strokeWidth="4" strokeLinecap="round"/>
              <path d="M4 28L16 42L28 28" stroke="#ff3333" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      )}

      {/* Desktop: positioned tooltip */}
      {!isMobile && (
        <div className="spotlight-tooltip" style={getTooltipStyle()} key={currentIndex}>
          <div className={`spotlight-tooltip-arrow ${arrowClass}`} />
          <div className="spotlight-tooltip-content">
            <h3 className="spotlight-tooltip-title">{stepTitle}</h3>
            <p className={`spotlight-tooltip-description${justPickedCampaign ? ' tooltip-celebrate' : ''}`} style={{ whiteSpace: 'pre-line' }}>{renderDescription(stepDescription, step.hoverImages)}</p>
            {renderMedia('spotlight-tooltip-video', 'spotlight-tooltip-audio')}
            {step.advanceOn === 'next-button' && (
              <button className="spotlight-next-btn" onClick={goNext} disabled={mustPickCampaign}
                style={mustPickCampaign ? {opacity: 0.5, cursor: 'not-allowed'} : undefined}>
                {mustPickCampaign ? '👆 בחרו קמפיין תחילה' : (currentIndex === steps.length - 1 ? 'סיום' : 'המשך →')}
              </button>
            )}
            {secondaryBtn && (
              <button className="spotlight-next-btn" onClick={() => goSkip(secondaryBtn.advanceBy)}>
                {secondaryBtn.label}
              </button>
            )}
            {renderHelpButton(step.title)}
            {renderSkipButton()}
            {steps.length > 1 && steps.length <= 5 && (
              <div className="spotlight-tooltip-footer">
                {renderDots('spotlight-tooltip-dots')}
                <span className="spotlight-tooltip-step-label">{stepLabel}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile: top banner for modal steps and click-to-reveal steps, bottom-sheet for others */}
      {isMobile && (step.modalId || step.customModalSelector || step.clickToReveal || step.mobileTopBanner) && (
        <div className="spotlight-top-banner" key={currentIndex} ref={bannerRef}>
          <div className="spotlight-top-banner-content">
            <svg className="spotlight-top-banner-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div className="spotlight-top-banner-text">
              <h3 className="spotlight-top-banner-title">{stepTitle}</h3>
              <p className={`spotlight-top-banner-description${justPickedCampaign ? ' tooltip-celebrate' : ''}`} style={{whiteSpace: 'pre-line'}}>{stepDescription}</p>
            </div>
            {step.advanceOn === 'next-button' && (
              <button className="spotlight-top-banner-next-btn" onClick={goNext} disabled={mustPickCampaign}
                style={mustPickCampaign ? {opacity: 0.5, cursor: 'not-allowed'} : undefined}>
                {mustPickCampaign ? '👆 בחרו קמפיין' : (currentIndex === steps.length - 1 ? 'סיום' : 'המשך →')}
              </button>
            )}
            {secondaryBtn && (
              <button className="spotlight-top-banner-next-btn" onClick={() => goSkip(secondaryBtn.advanceBy)}>
                {secondaryBtn.label}
              </button>
            )}
            {/* Icon-only here — the banner is a single tight row, so the full label
                from the tooltip/sheet wouldn't fit next to the next button. */}
            <button
              className="spotlight-top-banner-help"
              onClick={() => openTutorialHelp(step.title)}
              aria-label="צריכים עזרה? דברו איתנו"
              title="צריכים עזרה? דברו איתנו"
            >
              💬
            </button>
            {/* Exit. Always available to admin sessions so support can get out of a
                client's account without finishing the flow; for customers it appears only
                when the admin turned on tutorial_skip_enabled — the tour sets the account
                up (campaign, post, groups, profile), and quitting halfway leaves it
                half-configured with nothing telling them what's missing. */}
            {canSkipTutorial && (
              <button
                className="spotlight-top-banner-close"
                onClick={skipTutorial}
                aria-label="דלג על המדריך"
                title="דלג על המדריך"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="1" y1="1" x2="13" y2="13"/>
                  <line x1="13" y1="1" x2="1" y2="13"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      {isMobile && step.showDownArrow && targetRect && (
        <div className="spotlight-down-arrow" style={{
          left: targetRect.left + targetRect.width / 2,
          bottom: 'auto',
          top: targetRect.top - 50,
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <polyline points="19 12 12 19 5 12"/>
          </svg>
        </div>
      )}
      {isMobile && !step.modalId && !step.customModalSelector && !step.clickToReveal && !step.mobileTopBanner && (
        <div className="spotlight-bottom-sheet" key={currentIndex}>
          <div className="spotlight-bottom-sheet-handle" />
          <div className="spotlight-bottom-sheet-content">
            <h3 className="spotlight-bottom-sheet-title">{stepTitle}</h3>
            <p className={`spotlight-bottom-sheet-description${justPickedCampaign ? ' tooltip-celebrate' : ''}`} style={{ whiteSpace: 'pre-line' }}>{renderDescription(stepDescription, step.hoverImages)}</p>
            {renderMedia('spotlight-bottom-sheet-video', 'spotlight-bottom-sheet-audio')}
            {step.advanceOn === 'next-button' && (
              <button className="spotlight-next-btn" onClick={goNext} disabled={mustPickCampaign}
                style={mustPickCampaign ? {opacity: 0.5, cursor: 'not-allowed'} : undefined}>
                {mustPickCampaign ? '👆 בחרו קמפיין תחילה' : (currentIndex === steps.length - 1 ? 'סיום' : 'המשך →')}
              </button>
            )}
            {secondaryBtn && (
              <button className="spotlight-next-btn" onClick={() => goSkip(secondaryBtn.advanceBy)}>
                {secondaryBtn.label}
              </button>
            )}
            {renderHelpButton(step.title)}
            {renderSkipButton()}
            {steps.length > 1 && steps.length <= 5 && (
              <div className="spotlight-bottom-sheet-footer">
                {renderDots('spotlight-bottom-sheet-dots')}
                <span className="spotlight-bottom-sheet-step-label">{stepLabel}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scroll-to-step button when target is off-screen */}
      {!targetInViewport && targetRect && step.advanceOn === 'click' && (() => {
        const isAbove = targetRect.top < 0
        return (
          <button
            className={`spotlight-scroll-btn ${isAbove ? 'scroll-up' : 'scroll-down'}`}
            onClick={() => {
              const scrollSelector = step.scrollTarget || resolveTarget(step)
              const el = document.querySelector(scrollSelector) as HTMLElement | null
              el?.scrollIntoView({behavior: 'smooth', block: 'center'})
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={isAbove ? undefined : {transform: 'rotate(180deg)'}}>
              <path d="M8 13L8 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M3 8L8 3L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            קח אותי לשלב הנוכחי
          </button>
        )
      })()}
    </div>
  )

  return <>{createPortal(overlay, document.body)}{hoverImagePortal}</>
}
